/**
 * Demo Center deploy lane client (WQ-338) — /api/demos/publish/*.
 *
 * CloudFront rewrites origin 403/404 into the SPA's index.html with status 200,
 * so no response is trusted unless it is JSON: a non-JSON answer is "blocked",
 * never a success. Who may publish is read from `can_publish` on /status, never
 * inferred from a 403.
 */

export interface DeployedVersion {
  sha: string;
  built_at: string | null;
}

export interface PublishRun {
  id: number;
  status: string | null;
  conclusion: string | null;
  head_sha: string | null;
  created_at: string | null;
  html_url: string | null;
  /** WQ-348: false when this sha has no CAD ingest image (deploy lane v2 would fail). */
  rollback_available?: boolean;
}

export interface PublishStatus {
  can_publish: true;
  is_super_admin: boolean;
  deployed: DeployedVersion | null;
  latest_master_sha: string | null;
  last_run: PublishRun | null;
  github_error: string | null;
}

export interface Publisher {
  user_id: string;
  email: string;
  granted_by?: string;
  granted_at?: string;
}

export type PublishResult<T> =
  | { kind: 'ok'; data: T }
  | { kind: 'hidden' }
  | { kind: 'unconfigured'; isSuperAdmin: boolean }
  | { kind: 'blocked' }
  | { kind: 'error'; status: number; error: string };

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

const BASE = '/api/demos/publish';
export const ACTIVE_RUN_STATUSES: readonly string[] = ['queued', 'in_progress', 'waiting', 'requested', 'pending'];

export function isActiveRun(run: PublishRun | null | undefined): boolean {
  return !!run && ACTIVE_RUN_STATUSES.includes(run.status ?? '');
}

async function call<T>(authFetch: AuthFetch, path: string, init?: RequestInit): Promise<PublishResult<T>> {
  let response: Response;
  try {
    response = await authFetch(`${BASE}${path}`, init);
  } catch {
    return { kind: 'error', status: 0, error: 'network_error' };
  }
  const type = (response.headers?.get('content-type') ?? '').toLowerCase();
  if (!type.includes('application/json')) return { kind: 'blocked' };
  let body: Record<string, unknown>;
  try {
    body = await response.json();
  } catch {
    return { kind: 'blocked' };
  }
  if (response.status === 503 && body?.error === 'demos_pat_not_configured') {
    return { kind: 'unconfigured', isSuperAdmin: body.is_super_admin === true };
  }
  if (!response.ok) {
    const error = typeof body?.error === 'string' ? body.error : typeof body?.detail === 'string' ? body.detail : 'request_failed';
    return { kind: 'error', status: response.status, error };
  }
  return { kind: 'ok', data: body as T };
}

const jsonInit = (method: string, payload?: unknown): RequestInit => ({
  method,
  ...(payload === undefined
    ? {}
    : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }),
});

export async function getPublishStatus(authFetch: AuthFetch): Promise<PublishResult<PublishStatus>> {
  const result = await call<PublishStatus | { can_publish: false }>(authFetch, '/status');
  if (result.kind === 'ok' && result.data.can_publish !== true) return { kind: 'hidden' };
  return result as PublishResult<PublishStatus>;
}

export const triggerPublish = (authFetch: AuthFetch) =>
  call<{ status: string; ref: string; sha: string | null }>(authFetch, '/trigger', jsonInit('POST'));

export const getPublishHistory = (authFetch: AuthFetch) =>
  call<{ runs: PublishRun[] }>(authFetch, '/history');

export const rollbackPublish = (authFetch: AuthFetch, sha: string) =>
  call<{ status: string; rollback_to: string }>(authFetch, '/rollback', jsonInit('POST', { sha }));

export const listPublishers = (authFetch: AuthFetch) =>
  call<{ publishers: Publisher[] }>(authFetch, '/publishers');

export const addPublisher = (authFetch: AuthFetch, email: string) =>
  call<{ publishers: Publisher[] }>(authFetch, '/publishers', jsonInit('POST', { email }));

export const removePublisher = (authFetch: AuthFetch, userId: string) =>
  call<{ removed: boolean; publishers: Publisher[] }>(authFetch, `/publishers/${encodeURIComponent(userId)}`, jsonInit('DELETE'));
